import { useCallback } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Typography } from '@altermaind/web/ui';
import { Dialog } from '@/_shared/components/Dialog';
import { useNotification } from '@/_shared/hooks';
import { getDelegatesCall, postDelegateActivationEmailCall } from '@/organization/state';

type Props = {
  isOpen: boolean;
  closeDialog: () => void;
  userId: string;
  fullName: string;
};

export const ResendInviteDelegate = ({ isOpen, closeDialog, userId, fullName }: Props) => {
  const t = useTranslations('settings.tabs.delegates.resendInvite.dialog');
  const tShared = useTranslations();
  const { notify } = useNotification();
  const {
    execute: executePostDelegateActivationEmail,
    loading: loadingPostDelegateActivationEmail,
  } = postDelegateActivationEmailCall.hook();
  const { execute: executeGetDelegates } = getDelegatesCall.hook();

  const onConfirm = useCallback(() => {
    executePostDelegateActivationEmail({
      userId,
      successSaga: () => {
        notify(t('success'), 'success');
        closeDialog();
        executeGetDelegates({
          start: 0,
          length: 1000,
        });
      },
      errorSaga: () => {
        notify(tShared('errors.genericApiError'), 'error');
      },
    });
  }, [
    executePostDelegateActivationEmail,
    userId,
    notify,
    t,
    closeDialog,
    executeGetDelegates,
    tShared,
  ]);

  return (
    <Dialog
      maxWidth='700px'
      name='removeDelegate'
      open={isOpen}
      title={undefined}
      onClose={closeDialog}
      closeButton
    >
      <Box display='flex' flexDirection='column'>
        <Typography mt='none' variant='h3' color='primary/text' textAlign='center'>
          {t('title')}
        </Typography>
      </Box>

      <Box mb='xs' mt='m'>
        <Typography variant='p2/light' mb='xs' color='primary/text' textAlign='center'>
          {t('description', { fullName })}
        </Typography>
        <Typography variant='p2/light' fontWeight='bold' color='primary/text' textAlign='center'>
          {t('confirmation')}
        </Typography>
        <Box mt='s' alignItems='center' display='flex' flexDirection='row' justifyContent='center'>
          <Button variant='link/primary' onClick={() => closeDialog()} mr='s' mt='s'>
            {t('buttons.cancel')}
          </Button>
          <Button
            loading={loadingPostDelegateActivationEmail}
            disabled={loadingPostDelegateActivationEmail}
            mt='s'
            data-testid='resend-invite-button-delegate'
            onClick={onConfirm}
          >
            {t('buttons.confirm')}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
